import React, { useCallback, useEffect } from "react";

import { type FundDataWithType } from "@hsl/fund-explorer/metaSchemas";

type FundCardContextType = {
    openShareClassGroups: string[];
    toggleShareClass: (shareClassGroup: string) => void;
    fund: FundDataWithType;
};

export const FundCardContext = React.createContext<
    FundCardContextType | undefined
>(undefined);

interface Props {
    children: React.ReactNode;
    openGroups?: string[];
    fund: FundDataWithType;
}

export const FundCardProvider = ({ openGroups, fund, children }: Props) => {
    const [openShareClassGroups, setOpenShareClassGroups] = React.useState<
        string[]
    >(openGroups ? openGroups : []);

    useEffect(() => {
        if (Array.isArray(openGroups) && openGroups.length > 0) {
            setOpenShareClassGroups(openGroups);
        }
    }, [openGroups]);

    const toggleShareClass = useCallback(
        (shareClassGroup: string) => {
            if (!shareClassGroup) {
                return;
            }
            if (openShareClassGroups.includes(shareClassGroup)) {
                setOpenShareClassGroups((openGroups) =>
                    openGroups.filter((og) => og !== shareClassGroup),
                );
            } else {
                setOpenShareClassGroups((openGroups) => [
                    ...openGroups,
                    shareClassGroup,
                ]);
            }
        },
        [openShareClassGroups],
    );

    const contextValue = React.useMemo(
        () => ({
            openShareClassGroups,
            toggleShareClass,
            fund,
        }),
        [openShareClassGroups, toggleShareClass, fund],
    );

    return (
        <FundCardContext.Provider value={contextValue}>
            {children}
        </FundCardContext.Provider>
    );
};

export function useFundCardContext(fund?: FundCardContextType["fund"]) {
    const FundCard = React.useContext(FundCardContext);
    if (!FundCard && !fund) throw new Error("No FundCardProvider");
    return (
        FundCard ?? {
            fund,
            openShareClassGroups: [""],
            toggleShareClass() {
                return;
            },
        }
    );
}
